import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Settings2, Shield, Users, Building, Database, Megaphone, DollarSign,
  Headphones, Wallet, Wrench, BarChart3, Check, X, RefreshCw, Loader2,
  Info, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_ICONS = {
  crm: Database,
  marketing: Megaphone,
  sales: DollarSign,
  service: Headphones,
  finance: Wallet,
  hr: Users,
  operations: Shield,
  tools: Wrench,
  reports: BarChart3
};

const ModuleConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');
  
  // Organization config
  const [availableModules, setAvailableModules] = useState([]);
  const [orgConfig, setOrgConfig] = useState({ enabled_modules: [], disabled_modules: [] });
  
  // Role configs
  const [roleConfigs, setRoleConfigs] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleModules, setRoleModules] = useState([]);
  
  // User configs
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userOverrides, setUserOverrides] = useState({ enabled_modules: [], disabled_modules: [] });

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [modulesRes, orgRes, rolesRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/module-config/available`, { headers }),
        axios.get(`${API}/api/module-config/organization`, { headers }),
        axios.get(`${API}/api/module-config/roles`, { headers }),
        axios.get(`${API}/api/users/team`, { headers })
      ]);
      
      setAvailableModules(modulesRes.data.modules || []);
      setOrgConfig(orgRes.data);
      setRoleConfigs(rolesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error('Failed to fetch module config:', err);
      toast.error('Failed to load module configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveOrgConfig = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/module-config/organization`,
        null,
        { 
          headers,
          params: {
            enabled_modules: orgConfig.enabled_modules,
            disabled_modules: orgConfig.disabled_modules
          }
        }
      );
      toast.success('Organization module configuration saved');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleOrgModule = (moduleKey) => {
    setOrgConfig(prev => {
      const isEnabled = prev.enabled_modules.includes(moduleKey);
      if (isEnabled) {
        return {
          ...prev,
          enabled_modules: prev.enabled_modules.filter(m => m !== moduleKey),
          disabled_modules: [...prev.disabled_modules.filter(m => m !== moduleKey), moduleKey]
        };
      } else {
        return {
          ...prev,
          enabled_modules: [...prev.enabled_modules.filter(m => m !== moduleKey), moduleKey],
          disabled_modules: prev.disabled_modules.filter(m => m !== moduleKey)
        };
      }
    });
  };

  const openRoleDialog = (roleConfig) => {
    setSelectedRole(roleConfig);
    setRoleModules(roleConfig.enabled_modules || []);
    setRoleDialogOpen(true);
  };

  const saveRoleConfig = async () => {
    if (!selectedRole) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/module-config/roles/${selectedRole.role_name}`,
        null,
        {
          headers,
          params: { enabled_modules: roleModules }
        }
      );
      toast.success(`Role "${selectedRole.role_name}" configuration saved`);
      setRoleDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save role configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleRoleModule = (moduleKey) => {
    setRoleModules(prev => 
      prev.includes(moduleKey) 
        ? prev.filter(m => m !== moduleKey)
        : [...prev, moduleKey]
    );
  };

  const openUserDialog = async (user) => {
    setSelectedUser(user);
    try {
      const res = await axios.get(`${API}/api/module-config/users/${user.id}`, { headers });
      setUserOverrides({
        enabled_modules: res.data.enabled_modules || [],
        disabled_modules: res.data.disabled_modules || []
      });
    } catch (err) {
      setUserOverrides({ enabled_modules: [], disabled_modules: [] });
    }
    setUserDialogOpen(true);
  };

  const saveUserConfig = async () => {
    if (!selectedUser) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/module-config/users/${selectedUser.id}`,
        null,
        {
          headers,
          params: {
            enabled_modules: userOverrides.enabled_modules,
            disabled_modules: userOverrides.disabled_modules
          }
        }
      );
      toast.success(`User "${selectedUser.first_name} ${selectedUser.last_name}" configuration saved`);
      setUserDialogOpen(false);
    } catch (err) {
      toast.error('Failed to save user configuration');
    } finally {
      setSaving(false);
    }
  };

  const getModuleIcon = (key) => {
    const Icon = MODULE_ICONS[key] || Settings2;
    return <Icon className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="module-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6" />
            Module Configuration
          </h1>
          <p className="text-muted-foreground">
            Control which modules are available at organization, role, and user levels
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Module Access Hierarchy</p>
              <p className="text-sm text-blue-700">
                Organization settings define what's available company-wide. Role settings restrict access within enabled modules. 
                User settings provide individual overrides.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="roles">By Role</TabsTrigger>
          <TabsTrigger value="users">By User</TabsTrigger>
        </TabsList>

        {/* Organization Tab */}
        <TabsContent value="organization" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Organization-Wide Module Settings</CardTitle>
              <CardDescription>
                Enable or disable modules for the entire organization. Disabled modules will be hidden from all users.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {availableModules.map((module) => {
                  const isEnabled = orgConfig.enabled_modules.includes(module.key);
                  return (
                    <div 
                      key={module.key}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isEnabled 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-slate-200 bg-slate-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                            {getModuleIcon(module.key)}
                          </div>
                          <div>
                            <p className="font-medium">{module.name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2">{module.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => toggleOrgModule(module.key)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-6">
                <Button onClick={saveOrgConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  Save Organization Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Role-Based Module Access</CardTitle>
              <CardDescription>
                Configure which modules each role can access. Users will only see modules enabled for both their role and organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {roleConfigs.map((roleConfig) => (
                  <div 
                    key={roleConfig.role_name}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50 cursor-pointer"
                    onClick={() => openRoleDialog(roleConfig)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium capitalize">{roleConfig.role_name.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-muted-foreground">
                          {roleConfig.enabled_modules?.length || 0} modules enabled
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(roleConfig.enabled_modules || []).slice(0, 4).map(m => (
                        <Badge key={m} variant="secondary" className="text-xs">
                          {m}
                        </Badge>
                      ))}
                      {(roleConfig.enabled_modules || []).length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{roleConfig.enabled_modules.length - 4}
                        </Badge>
                      )}
                      <Button variant="ghost" size="sm">Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User-Specific Overrides</CardTitle>
              <CardDescription>
                Grant or restrict module access for individual users. These settings override role-based access.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {users.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-medium">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{user.email}</span>
                            <Badge variant="outline" className="text-xs capitalize">
                              {user.role?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => openUserDialog(user)}>
                        Configure
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Role Edit Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Configure Modules for {selectedRole?.role_name?.replace(/_/g, ' ')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Select which modules users with this role can access:
            </p>
            <div className="space-y-2">
              {availableModules.map((module) => {
                const isOrgEnabled = orgConfig.enabled_modules.includes(module.key);
                const isRoleEnabled = roleModules.includes(module.key);
                
                return (
                  <div 
                    key={module.key}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      !isOrgEnabled ? 'opacity-50 bg-slate-50' : ''
                    }`}
                  >
                    <Checkbox
                      checked={isRoleEnabled}
                      onCheckedChange={() => toggleRoleModule(module.key)}
                      disabled={!isOrgEnabled}
                    />
                    <div className="flex items-center gap-2 flex-1">
                      {getModuleIcon(module.key)}
                      <span className="font-medium">{module.name}</span>
                    </div>
                    {!isOrgEnabled && (
                      <Badge variant="destructive" className="text-xs">
                        Org Disabled
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRoleConfig} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Role Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Edit Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Configure Modules for {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">User-level overrides take priority over role settings</span>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Additional Modules (Grant Access)</Label>
              <p className="text-xs text-muted-foreground mb-2">Enable modules beyond what their role allows</p>
              <div className="flex flex-wrap gap-2">
                {availableModules.map((module) => {
                  const isEnabled = userOverrides.enabled_modules.includes(module.key);
                  return (
                    <Badge
                      key={module.key}
                      variant={isEnabled ? "default" : "outline"}
                      className={`cursor-pointer ${isEnabled ? 'bg-green-600' : ''}`}
                      onClick={() => {
                        setUserOverrides(prev => ({
                          ...prev,
                          enabled_modules: isEnabled
                            ? prev.enabled_modules.filter(m => m !== module.key)
                            : [...prev.enabled_modules, module.key],
                          disabled_modules: prev.disabled_modules.filter(m => m !== module.key)
                        }));
                      }}
                    >
                      {isEnabled && <Check className="h-3 w-3 mr-1" />}
                      {module.name}
                    </Badge>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Restricted Modules (Revoke Access)</Label>
              <p className="text-xs text-muted-foreground mb-2">Disable modules even if their role allows them</p>
              <div className="flex flex-wrap gap-2">
                {availableModules.map((module) => {
                  const isDisabled = userOverrides.disabled_modules.includes(module.key);
                  return (
                    <Badge
                      key={module.key}
                      variant={isDisabled ? "destructive" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        setUserOverrides(prev => ({
                          ...prev,
                          disabled_modules: isDisabled
                            ? prev.disabled_modules.filter(m => m !== module.key)
                            : [...prev.disabled_modules, module.key],
                          enabled_modules: prev.enabled_modules.filter(m => m !== module.key)
                        }));
                      }}
                    >
                      {isDisabled && <X className="h-3 w-3 mr-1" />}
                      {module.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveUserConfig} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save User Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModuleConfigPage;
